import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';
import { AVATARS, getLocalizedAvatarInfo } from '../../config/avatarMapping';
import { getIdToken, isAuthenticated } from '../../services/cognitoService';
import { purchaseSponsorship } from '../../services/revenuecatService';
import { getAllSponsorshipsPublic, getSponsorInfo } from '../../services/sponsorService';
import { logger } from '../../utils/logger';
import './SponsorDetail.css';

interface SponsorInfo {
  userName: string;
  displayName?: string;
  amount: number;
  currency: string;
  fundedAt: string;
  notes?: string;
}

const SponsorDetail: React.FC = () => {
  const { sponsorId } = useParams<{ sponsorId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isLoggedIn = isAuthenticated();

  const [sponsorInfo, setSponsorInfo] = useState<SponsorInfo | null>(null);
  const [isSponsored, setIsSponsored] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Sponsorship form state
  const [showSponsorshipForm, setShowSponsorshipForm] = useState(false);
  const [sponsorDisplayName, setSponsorDisplayName] = useState('');
  const [sponsorNotes, setSponsorNotes] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  // Get companion data from avatar mapping
  const companion = useMemo(() => {
    if (!sponsorId) return null;
    const avatar = AVATARS[sponsorId];
    if (!avatar) return null;
    return getLocalizedAvatarInfo(avatar, i18n.language);
  }, [sponsorId, i18n.language]);

  // Fetch sponsor info
  useEffect(() => {
    const fetchSponsorInfo = async () => {
      if (!sponsorId) {
        setIsLoading(false);
        return;
      }

      try {
        if (isLoggedIn) {
          const response = await getSponsorInfo(sponsorId);
          setIsSponsored(response.isSponsored);
          if (response.sponsor) {
            setSponsorInfo({
              userName: response.sponsor.userName,
              displayName: response.sponsor.userName,
              amount: response.sponsor.amount,
              currency: response.sponsor.currency,
              fundedAt: response.sponsor.fundedAt,
              notes: response.sponsor.notes,
            });
          }
        } else {
          const response = await getAllSponsorshipsPublic();
          const sponsorship = response.sponsorships.find((s) => s.avatarId === sponsorId);
          if (sponsorship) {
            setIsSponsored(true);
            setSponsorInfo({
              userName: sponsorship.sponsor.displayName,
              displayName: sponsorship.sponsor.displayName,
              amount: 2500, // Fixed sponsorship amount fallback
              currency: 'USD',
              fundedAt: sponsorship.sponsor.fundedAt,
              notes: sponsorship.sponsor.notes || undefined,
            });
          } else {
            setIsSponsored(false);
          }
        }
      } catch (error) {
        logger.error('SponsorDetail', 'Failed to fetch sponsor info:', error as Error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSponsorInfo();
  }, [sponsorId, isLoggedIn]);

  const handleDonateClick = () => {
    if (!isLoggedIn) {
      const currentPath = location.pathname;
      navigate(`/login?redirect=${encodeURIComponent(currentPath)}`, {
        state: { from: currentPath },
      });
      return;
    }
    setSponsorDisplayName('');
    setSponsorNotes('');
    setIsAnonymous(false);
    setPaymentError(null);
    setShowSponsorshipForm(true);
  };

  const handleSubmitSponsorship = async () => {
    if (!sponsorId || !companion) return;

    setIsProcessingPayment(true);
    setPaymentError(null);

    try {
      const authToken = await getIdToken();
      if (!authToken) {
        throw new Error('Not authenticated');
      }

      const result = await purchaseSponsorship(
        sponsorId,
        companion.displayName,
        isAnonymous ? undefined : sponsorDisplayName || undefined,
        sponsorNotes || undefined,
        !isAnonymous,
        authToken,
      );

      if (result.success) {
        setShowSponsorshipForm(false);
        setIsSponsored(true);
        alert(result.message);
        // Refresh sponsor info
        const response = await getSponsorInfo(sponsorId);
        if (response.sponsor) {
          setSponsorInfo({
            userName: response.sponsor.userName,
            displayName: response.sponsor.userName,
            amount: response.sponsor.amount,
            currency: response.sponsor.currency,
            fundedAt: response.sponsor.fundedAt,
            notes: response.sponsor.notes,
          });
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === 'PURCHASE_CANCELLED') {
          setShowSponsorshipForm(false);
        } else {
          setPaymentError(error.message);
        }
      } else {
        logger.error('SponsorDetail', 'Donation error:', error as Error);
        setPaymentError(
          t('sponsorship.paymentError', 'Failed to process payment. Please try again.'),
        );
      }
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  if (!companion) {
    return (
      <div className="sponsor-detail-page-wrapper">
        <Header isLoggedIn={isLoggedIn} />
        <main className="sponsor-detail-main">
          <div className="not-found">
            <h1>{t('sponsorPage.companionNotFound', 'Companion not found')}</h1>
            <Link to="/sponsor" className="back-link-btn">
              {t('sponsorPage.backToCompanions', 'Back to Companions')}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="sponsor-detail-page-wrapper">
      <div className="bg-accent-shape" />
      <Header isLoggedIn={isLoggedIn} />

      <main className="sponsor-detail-main">
        {/* Breadcrumb Navigation */}
        <nav className="breadcrumb-nav">
          <button
            type="button"
            className="back-btn"
            onClick={() => navigate('/sponsor')}
            aria-label="Go back"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <title>{t('common.back', 'Back')}</title>
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="breadcrumb-links">
            <Link to="/" className="breadcrumb-link">
              {t('header.home', 'Home')}
            </Link>
            <svg
              className="breadcrumb-separator"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <title>Separator</title>
              <path d="M9 18l6-6-6-6" />
            </svg>
            <Link to="/sponsor" className="breadcrumb-link">
              {t('header.sponsor', 'Sponsor')}
            </Link>
            <svg
              className="breadcrumb-separator"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <title>Separator</title>
              <path d="M9 18l6-6-6-6" />
            </svg>
            <span className="breadcrumb-current">{companion.displayName}</span>
          </div>
        </nav>

        {/* Hero Section with Avatar */}
        <section className="companion-hero">
          <div className="companion-avatar-container">
            <div className="avatar-glow" />
            <img
              src={companion.image}
              alt={companion.displayName}
              className={`companion-avatar ${!isSponsored ? 'sponsorable' : ''}`}
              onClick={!isSponsored ? handleDonateClick : undefined}
              role={!isSponsored ? 'button' : undefined}
              tabIndex={!isSponsored ? 0 : undefined}
              onKeyDown={(e) => {
                if (!isSponsored && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  handleDonateClick();
                }
              }}
            />
            <div className="avatar-gradient" />
          </div>

          <div className="companion-hero-info">
            <div className="about-label-container">
              <div className="label-line" />
              <span className="about-label">{t('sponsorPage.sponsorTitle', 'Sponsor')}</span>
            </div>

            <h1 className="companion-name-english">{companion.displayName}</h1>
          </div>
        </section>

        {/* Info Card - Roles & Lifespan */}
        <section className="companion-info-card">
          <div className="info-card-content">
            <div className="info-roles-section">
              <div className="info-section-header">
                <span className="info-section-title">{t('companion.roles', 'Roles')}</span>
              </div>
              <div className="roles-badges">
                {companion.displayTags.map((tag: string) => (
                  <span key={tag} className="role-badge">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="info-divider" />

            <div className="info-lifespan-section">
              <div className="info-section-header">
                <span className="info-section-title lifespan-title">
                  {t('companion.lifespan', 'Life span')}
                </span>
              </div>
              <span className="lifespan-value">{companion.lifespan}</span>
            </div>
          </div>
        </section>

        {/* Biography Section */}
        {companion.displayDescription && (
          <section className="companion-biography">
            <div className="biography-header">
              <h3 className="biography-title">{t('companion.biography', 'Biography')}</h3>
            </div>
            <p className="biography-text">{companion.displayDescription}</p>
          </section>
        )}

        {/* Support Card */}
        <section className="support-section">
          <div className="support-card">
            <div className="support-info">
              <h3 className="support-title">
                {t('sponsorPage.supportProject', "Support This Companion's Project")}
              </h3>
              <p className="support-description">
                {t('sponsorPage.fixedDonation', 'Fixed sponsorship amount:')}
              </p>
              <span className="donation-amount">$2,500.00 USD</span>
            </div>
            <button
              type="button"
              className={`donate-now-btn ${isSponsored ? 'disabled' : ''}`}
              onClick={handleDonateClick}
              disabled={isSponsored || isLoading}
            >
              {isSponsored
                ? t('sponsorPage.donated', 'Sponsored')
                : t('sponsorPage.donateNow', 'Sponsor Now')}
            </button>
          </div>
        </section>

        {/* Donor Section (if sponsored) */}
        {isSponsored && sponsorInfo && (
          <section className="donor-section">
            <div className="section-header">
              <div className="header-line" />
              <h2 className="section-title">{t('sponsorPage.donor', 'Donor')}</h2>
            </div>
            <div className="donor-card">
              <div className="donor-info">
                <span className="donor-name">{sponsorInfo.displayName || 'Anonymous'}</span>
                {sponsorInfo.notes && <span className="donor-note">"{sponsorInfo.notes}"</span>}
                <span className="donor-date">{formatDate(sponsorInfo.fundedAt)}</span>
              </div>
              <span className="donor-amount">${sponsorInfo.amount.toLocaleString()} USD</span>
            </div>
          </section>
        )}
      </main>

      <Footer />

      {/* Sponsorship Form Modal */}
      {showSponsorshipForm && (
        <button
          type="button"
          className="sponsorship-form-overlay"
          onClick={() => !isProcessingPayment && setShowSponsorshipForm(false)}
          aria-label={t('common.close', 'Close')}
        >
          <div
            className="sponsorship-form-modal"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              className="sponsorship-form-close"
              onClick={() => setShowSponsorshipForm(false)}
              disabled={isProcessingPayment}
              aria-label={t('common.close', 'Close')}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <title>{t('common.close', 'Close')}</title>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <h2 className="sponsorship-form-title">
              {t('sponsorPage.completeSponsorshipTitle', 'Complete Your Sponsorship')}
            </h2>

            <p className="sponsorship-form-subtitle">
              {t('sponsorPage.sponsoring', 'You are sponsoring')}{' '}
              <strong>{companion.displayName}</strong>
            </p>

            <div className="sponsorship-form-fields">
              <div className="sponsorship-form-field">
                <label htmlFor="sponsor-anonymous" className="sponsorship-checkbox-label">
                  <input
                    type="checkbox"
                    id="sponsor-anonymous"
                    checked={isAnonymous}
                    onChange={(e) => setIsAnonymous(e.target.checked)}
                    disabled={isProcessingPayment}
                  />
                  <span>{t('sponsorPage.keepAnonymous', 'Keep my sponsorship anonymous')}</span>
                </label>
              </div>

              {!isAnonymous && (
                <div className="sponsorship-form-field">
                  <label htmlFor="sponsor-name">
                    {t('sponsorPage.displayNameLabel', 'Display Name (optional)')}
                  </label>
                  <input
                    type="text"
                    id="sponsor-name"
                    value={sponsorDisplayName}
                    onChange={(e) => setSponsorDisplayName(e.target.value)}
                    placeholder={t(
                      'sponsorPage.displayNamePlaceholder',
                      'How you want to be recognized',
                    )}
                    maxLength={50}
                    disabled={isProcessingPayment}
                  />
                </div>
              )}

              <div className="sponsorship-form-field">
                <label htmlFor="sponsor-notes">
                  {t('sponsorPage.notesLabel', 'Add a note (optional)')}
                </label>
                <textarea
                  id="sponsor-notes"
                  value={sponsorNotes}
                  onChange={(e) => setSponsorNotes(e.target.value)}
                  placeholder={t(
                    'sponsorPage.notesPlaceholder',
                    'In memory of... / May this benefit...',
                  )}
                  maxLength={200}
                  rows={3}
                  disabled={isProcessingPayment}
                />
                <span className="sponsorship-form-char-count">{sponsorNotes.length}/200</span>
              </div>
            </div>

            {paymentError && <div className="sponsorship-form-error">{paymentError}</div>}

            <div className="sponsorship-form-actions">
              <button
                type="button"
                className="sponsorship-form-cancel"
                onClick={() => setShowSponsorshipForm(false)}
                disabled={isProcessingPayment}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                className="sponsorship-form-submit"
                onClick={handleSubmitSponsorship}
                disabled={isProcessingPayment}
              >
                {isProcessingPayment
                  ? t('sponsorPage.processing', 'Processing...')
                  : t('sponsorPage.payNow', 'Pay $2,500 USD')}
              </button>
            </div>
          </div>
        </button>
      )}
    </div>
  );
};

export default SponsorDetail;
